import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type CampaignFormValues } from "@/components/campaign/campaign-form";
import {
  type ChannelType,
  CampaignsService,
  type CampaignType,
  type CreateCampaignAdPlacement,
  type CreateCampaignInput,
  type CreateCampaignProductRule,
} from "@/services/campaigns.service";
import { getBudgetSummary, parseBudgetAmount } from "@/utils/budget";
import { adsHttpClient } from "@/utils/ads.http";
import { type PlacementChannelOption } from "./use-placement-catalog";

const campaignsService = new CampaignsService(adsHttpClient);

type CreateCampaignVariables = {
  values: CampaignFormValues;
  channels: PlacementChannelOption[];
};

function getCampaignType(pricingModel: string): CampaignType {
  switch (pricingModel) {
    case "cpc":
      return "CPC";
    case "cpi":
    case "cpm":
      return "CPI";
    default:
      throw new Error(`Unsupported pricing model: ${pricingModel}`);
  }
}

function getAdPlacements(
  values: CampaignFormValues,
  channels: PlacementChannelOption[],
): CreateCampaignAdPlacement[] {
  return channels
    .filter((channel) => values.placementChannels.includes(channel.id))
    .flatMap((channel) =>
      channel.zones
        .filter((zone) =>
          values.placementZones.includes(`${channel.id}.${zone.id}`),
        )
        .map((zone) => ({
          channel: channel.id as ChannelType,
          zoneId: zone.id,
        })),
    );
}

function toCreateCampaignInput(
  values: CampaignFormValues,
  channels: PlacementChannelOption[],
): CreateCampaignInput {
  if (!values.brandId) {
    throw new Error("A brand must be selected before launching.");
  }

  const adPlacements = getAdPlacements(values, channels);

  if (adPlacements.length === 0) {
    throw new Error("Select at least one placement before launching.");
  }

  const { dailyBudget } = getBudgetSummary(values);
  let dailyPacing = 0;

  if (values.pacing === "custom") {
    dailyPacing = parseBudgetAmount(values.dailyPacing);
  } else if (values.pacing === "even") {
    dailyPacing = dailyBudget;
  }
  const pacingRules =
    dailyPacing > 0
      ? [
          {
            window: "DAY" as const,
            maxCredits: Math.round(dailyPacing * 1000),
          },
        ]
      : undefined;
  const initialBudget = values.totalBudget
    ? Math.round(Number(values.totalBudget) * 1000)
    : undefined;
  const productRules: CreateCampaignProductRule[] = [
    {
      dimension: "BRAND",
      operator: "IN",
      values: [values.brandId],
    },
  ];

  if (values.minimumPrice.trim().length > 0) {
    productRules.push({
      dimension: "PRICE",
      operator: "GREATER_OR_EQUAL",
      values: [values.minimumPrice],
    });
  }

  return {
    name: values.campaignName,
    type: getCampaignType(values.pricingModel),
    active: true,
    initialBudget,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    startDate: values.budgetStartDate || null,
    endDate: values.budgetEndDate || null,
    eligibilityFilter: {
      rules: [
        {
          dimension: "STATE",
          operator: "IN",
          values: [values.marketState],
        },
      ],
    },
    productFilter: {
      rules: productRules,
    },
    pacingRules,
    adPlacements,
  };
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ values, channels }: CreateCampaignVariables) => {
      const input = toCreateCampaignInput(values, channels);
      return campaignsService.create(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
}
